/**
 * Registration Page
 * Public registration page with trial signup
 */

import React from 'react';
import { Link } from 'react-router-dom';
import RegistrationForm from '../components/auth/RegistrationForm';
import { ArrowLeft } from 'lucide-react';
import { trackFunnel } from '../services/analytics';
import { useEffect } from 'react';

export default function RegisterPage() {
  useEffect(() => {
    trackFunnel('user_onboarding', 'view_register_page', 1);
  }, []);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-white via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                className="h-10 w-auto"
                src="/logo.jpg"
                alt="CanvasEstate logo"
              />
              <div className="ml-2 flex flex-col">
                <span className="text-xl font-bold text-brand-secondary leading-none">CanvasEstate</span>
                <span className="text-[0.6rem] text-brand-primary font-bold uppercase tracking-wider">Let's Canvas Your Estate</span>
              </div>
            </Link>
            <Link
              to="/login"
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Value Proposition */}
          <div className="order-2 lg:order-1">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Start Managing Your Property Portfolio Today
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Join Australian property investors who trust CanvasEstate to track their portfolios,
              analyze cashflow, and make data-driven investment decisions.
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Track Multiple Properties</h3>
                  <p className="text-sm text-gray-600">
                    Monitor income, expenses, and performance across your entire portfolio
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI-Powered Analysis</h3>
                  <p className="text-sm text-gray-600">
                    Get personalized insights and recommendations from our financial AI
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Simple, Transparent Pricing</h3>
                  <p className="text-sm text-gray-600">
                    Free forever for up to 10 properties. Commercial plans from $49/mo.
                  </p>
                </div>
              </div>
            </div>

            {/* Social Proof */}
            <div className="p-4 bg-white rounded-lg border border-gray-200 inline-block">
              <p className="text-sm text-gray-600">
                Trusted by <span className="font-semibold text-gray-900">Australian property investors</span>
              </p>
            </div>

            {/* Pricing Link */}
            <div className="mt-8">
              <Link
                to="/pricing"
                className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
              >
                View detailed pricing →
              </Link>
            </div>
          </div>

          {/* Right Column - Registration Form */}
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                Create Your Account
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Get started for free today
              </p>

              <RegistrationForm />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-500">
            <p>
              © 2025 CanvasEstate. All rights reserved. |{' '}
              <Link to="/terms" className="hover:text-gray-700">Terms</Link> |{' '}
              <Link to="/privacy" className="hover:text-gray-700">Privacy</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
